// Pure functions for Roman numeral conversion and question generation.
// All functions are side-effect free and unit-testable.

// Standard roman numerals (1–3999)
const STANDARD_TABLE: [number, string][] = [
  [1000, 'M'],
  [900,  'CM'],
  [500,  'D'],
  [400,  'CD'],
  [100,  'C'],
  [90,   'XC'],
  [50,   'L'],
  [40,   'XL'],
  [10,   'X'],
  [9,    'IX'],
  [5,    'V'],
  [4,    'IV'],
  [1,    'I'],
]

// Extended table for 4000–10000 using vinculum notation.
// Vinculum groups are wrapped in [square brackets] so that the RomanDisplay
// component can render a single CSS overline spanning the whole group.
// M is NOT used above 3999.
const EXTENDED_TABLE: [number, string][] = [
  [10000, '[X]'],
  [9000,  '[IX]'],
  [8000,  '[VIII]'],
  [7000,  '[VII]'],
  [6000,  '[VI]'],
  [5000,  '[V]'],
  [4000,  '[IV]'],
  ...STANDARD_TABLE,
]

export function toRoman(n: number): string {
  if (n < 1 || n > 10000 || !Number.isInteger(n)) return ''
  const table = n > 3999 ? EXTENDED_TABLE : STANDARD_TABLE
  let result = ''
  let remaining = n
  for (const [value, numeral] of table) {
    while (remaining >= value) {
      result += numeral
      remaining -= value
    }
  }
  return result
}

// Strip bracket notation before parsing (used for fromRoman / checkRomanInput).
function stripBrackets(s: string): string {
  return s.replace(/\[([^\]]+)\]/g, '$1')
}

export function fromRoman(s: string): number {
  // Only try vinculum lookup when the input itself uses bracket notation
  if (s.includes('[')) {
    const clean = stripBrackets(s).trim().toUpperCase()
    for (const [value, numeral] of EXTENDED_TABLE) {
      if (clean === stripBrackets(numeral)) return value
    }
  }
  // Standard subtractive decoding (1–3999)
  const clean = s.trim().toUpperCase()
  const VALUES: Record<string, number> = { I: 1, V: 5, X: 10, L: 50, C: 100, D: 500, M: 1000 }
  let result = 0
  let prev = 0
  for (let i = clean.length - 1; i >= 0; i--) {
    const curr = VALUES[clean[i]] ?? 0
    result += curr < prev ? -curr : curr
    prev = curr
  }
  return result
}

export type Difficulty = 'easy' | 'medium' | 'hard'

export const DIFFICULTY_RANGE: Record<Difficulty, [number, number]> = {
  easy:   [1, 20],
  medium: [1, 1000],
  hard:   [1, 10000],
}

export const DIFFICULTY_CHOICES: Record<Difficulty, number> = {
  easy:   4,
  medium: 6,
  hard:   0, // free-text
}

export const QUESTIONS_PER_GAME = 10

export interface RomanQuestion {
  arabic: number
  roman:  string   // may contain [bracket] groups for vinculum notation
}

export function generateQuestion(difficulty: Difficulty, usedNumbers: Set<number>): RomanQuestion {
  const [min, max] = DIFFICULTY_RANGE[difficulty]
  const range = max - min + 1
  let arabic: number
  let attempts = 0
  do {
    arabic = min + Math.floor(Math.random() * range)
    attempts++
  } while (usedNumbers.has(arabic) && attempts < range)
  return { arabic, roman: toRoman(arabic) }
}

export function generateDistractors(
  correct: RomanQuestion,
  count: number,
  difficulty: Difficulty,
): RomanQuestion[] {
  const [min, max] = DIFFICULTY_RANGE[difficulty]
  const distractors: RomanQuestion[] = []
  const usedArabics = new Set([correct.arabic])

  // Prefer nearby numbers for plausible distractors
  const candidates: number[] = []
  const spread = difficulty === 'easy' ? 5 : difficulty === 'medium' ? 100 : 500
  for (let offset = -spread; offset <= spread; offset++) {
    const candidate = correct.arabic + offset
    if (candidate >= min && candidate <= max && candidate !== correct.arabic) {
      candidates.push(candidate)
    }
  }
  // Shuffle candidates
  for (let i = candidates.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [candidates[i], candidates[j]] = [candidates[j], candidates[i]]
  }

  for (const arabic of candidates) {
    if (distractors.length >= count - 1) break
    if (!usedArabics.has(arabic)) {
      usedArabics.add(arabic)
      distractors.push({ arabic, roman: toRoman(arabic) })
    }
  }

  // Fill remaining from random if spread wasn't enough
  let safety = 0
  while (distractors.length < count - 1 && safety < 500) {
    safety++
    const arabic = min + Math.floor(Math.random() * (max - min + 1))
    if (!usedArabics.has(arabic)) {
      usedArabics.add(arabic)
      distractors.push({ arabic, roman: toRoman(arabic) })
    }
  }

  return distractors
}

export function buildOptions(
  correct: RomanQuestion,
  count: number,
  difficulty: Difficulty,
): RomanQuestion[] {
  const distractors = generateDistractors(correct, count, difficulty)
  const all = [correct, ...distractors]
  // Shuffle
  for (let i = all.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [all[i], all[j]] = [all[j], all[i]]
  }
  return all
}

export function generateAllQuestions(difficulty: Difficulty): RomanQuestion[] {
  const used = new Set<number>()
  const questions: RomanQuestion[] = []
  for (let i = 0; i < QUESTIONS_PER_GAME; i++) {
    const q = generateQuestion(difficulty, used)
    used.add(q.arabic)
    questions.push(q)
  }
  return questions
}

export function normalizeRomanInput(s: string): string {
  return s.trim().toUpperCase().replace(/\s+/g, '')
}

// Rejects non-canonical forms (e.g. IIII, VX) by round-tripping through toRoman.
export function checkRomanInput(input: string, question: RomanQuestion): boolean {
  const normalized = normalizeRomanInput(input)
  if (normalized === question.roman) return true
  const parsed = fromRoman(normalized)
  if (parsed !== question.arabic || parsed <= 0) return false
  return toRoman(parsed) === normalized
}

// Hard mode: user sees the roman numeral and types the arabic number.
export function checkArabicInput(input: string, question: RomanQuestion): boolean {
  const trimmed = input.trim()
  if (!/^\d+$/.test(trimmed)) return false
  return parseInt(trimmed, 10) === question.arabic
}
